/**
 * ProposalKanbanBoard - Board do Kanban de Propostas (colunas fixas por situação)
 *
 * Somente leitura (sem DndContext): os cards migram de coluna automaticamente
 * conforme a situação muda no backend, via Realtime centralizado.
 *
 * @version 1.2.0
 * RELEASE: 2026-09-26 v1.2.0 - Passa a usar a superfície compartilhada dos
 *          Kanbans somente leitura, preservando integralmente o visual existente
 * RELEASE: 2026-08-25 v1.1.0 - Paridade com o Kanban de Contratos: scroll surface externa
 *          (overflow-hidden + overscroll-contain + bloqueio do wheel vertical), ScrollIndicators
 *          e faixa de colunas com altura explícita — o scroll passa a ocorrer DENTRO de cada
 *          coluna, e não na página inteira
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import React from 'react';
import { ReadonlyKanbanBoard } from '@/components/kanban/ReadonlyKanbanBoard';
import { useProposalKanbanCounts } from '@/hooks/useProposalKanbanCounts';
import { useProposalsKanbanRealtime } from '@/hooks/useProposalsKanbanRealtime';
import {
  useInvalidateProposalKanban,
  type ProposalFilters,
} from '@/hooks/useProposalKanbanColumn';
import { PROPOSAL_KANBAN_COLUMNS } from './proposalKanbanColumns';
import { ProposalKanbanColumn } from './ProposalKanbanColumn';

interface ProposalKanbanBoardProps {
  filters: ProposalFilters;
  onOpenProposal: (proposalId: string) => void;
  onOpenDeal: (dealId: string) => void;
}

export function ProposalKanbanBoard({
  filters,
  onOpenProposal,
  onOpenDeal,
}: ProposalKanbanBoardProps) {
  // Contagens/valores de todas as colunas em uma única chamada
  const { countsMap } = useProposalKanbanCounts(filters);
  // Canal único de Realtime enquanto o board estiver montado
  useProposalsKanbanRealtime(true);
  const invalidateKanban = useInvalidateProposalKanban();

  return (
    <ReadonlyKanbanBoard>
      {PROPOSAL_KANBAN_COLUMNS.map((column) => (
        <ProposalKanbanColumn
          key={column.status}
          column={column}
          filters={filters}
          totals={countsMap.get(column.status)}
          onOpenProposal={onOpenProposal}
          onOpenDeal={onOpenDeal}
          onActionComplete={invalidateKanban}
        />
      ))}
    </ReadonlyKanbanBoard>
  );
}
